import { Check, Sparkles } from "lucide-react";
import { useEffect, useState } from "react";

import { Button, Dialog, DialogContent, DialogDescription, DialogTitle } from "@link/ui";

import { api } from "../lib/api";
import { updateIsBlocked } from "../lib/update-activity";

type Release = Awaited<ReturnType<typeof api.releases.current>>["releases"][number];
const CLIENT_LINK_RELEASE = "0.2.0";

/** Opens once per account and release, after the authenticated shell mounts. */
export function ReleaseChangelog() {
  const [releases, setReleases] = useState<Release[]>([]);
  const [currentVersion, setCurrentVersion] = useState(CLIENT_LINK_RELEASE);
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    let cancelled = false;
    void api.releases.current().then((result) => {
      if (cancelled || !result.needs_acknowledgement) return;
      setReleases([...result.releases]);
      setCurrentVersion(result.current_version);
      setOpen(true);
    }).catch(() => undefined);
    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    const check = (): void => {
      void api.releases.current().then((result) => {
        // A server-side UI deployment must not reload an active/ringing call
        // or race the native updater's restart confirmation.
        if (updateIsBlocked()) return;
        if (result.current_version === CLIENT_LINK_RELEASE) return;
        const key = `link:version-refresh:${result.current_version}`;
        if (window.sessionStorage.getItem(key) === "1") return;
        window.sessionStorage.setItem(key, "1");
        const url = new URL(window.location.href);
        url.searchParams.set("link_version", result.current_version);
        window.location.replace(url.toString());
      }).catch(() => undefined);
    };
    const interval = window.setInterval(check, 45_000);
    check();
    const onVisibility = (): void => { if (document.visibilityState === "visible") check(); };
    window.addEventListener("focus", check);
    document.addEventListener("visibilitychange", onVisibility);
    return () => {
      window.clearInterval(interval);
      window.removeEventListener("focus", check);
      document.removeEventListener("visibilitychange", onVisibility);
    };
  }, []);

  const finish = async (): Promise<void> => {
    if (releases.length === 0) return;
    setBusy(true);
    try { await api.releases.acknowledge(currentVersion); setOpen(false); }
    finally { setBusy(false); }
  };

  if (releases.length === 0) return null;
  const latest = releases.find((release) => release.version === currentVersion) ?? releases[0]!;
  return (
    <Dialog open={open} onOpenChange={(next) => { if (!next && !busy) void finish(); }}>
      <DialogContent className="max-h-[min(760px,92dvh)] max-w-2xl overflow-y-auto rounded-[28px] border-cloud-200 bg-app-canvas p-0">
        <div className="bg-strawberry-gradient px-6 py-7 text-white sm:px-8">
          <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-white/20 ring-1 ring-white/30"><Sparkles className="h-6 w-6" /></div>
          <DialogTitle className="mt-5 text-3xl font-bold text-white">{latest.title}</DialogTitle>
          <DialogDescription className="mt-2 text-sm leading-relaxed text-white/85">{latest.summary}</DialogDescription>
        </div>
        <div className="space-y-6 px-6 py-7 sm:px-8">
          {releases.map((release) => <article key={release.version} className="rounded-2xl border border-cloud-200 bg-white/70 p-5"><p className="text-xs font-black uppercase tracking-wider text-strawberry-600">{release.title}</p><p className="mt-1 text-sm text-cloud-500">{release.summary}</p><div className="mt-5 space-y-5">{release.sections.map((section) => (<section key={section.title}><h2 className="font-bold text-cloud-900">{section.title}</h2><ul className="mt-3 space-y-2.5">{section.items.map((item) => <li key={item} className="flex gap-3 text-sm leading-relaxed text-cloud-600"><span className="mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-strawberry-100 text-strawberry-700"><Check className="h-3 w-3" /></span>{item}</li>)}</ul></section>))}</div></article>)}
          <Button className="w-full" disabled={busy} onClick={() => void finish()}>Start using Link {currentVersion.replace(/\.0$/, "")}</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
